import React, {useEffect, useState} from 'react';
import {Col, Layout, message, Row, Spin} from "antd";

import {ProfileOutput} from "@unimaze/api-clients/lib/wapi2";

import poweredByUnimazeLogo from "./resources/images/powered_by_unimaze.png"
import sjovalogorgb_medium from "./resources/images/sjovalogorgb_medium.png"
import reklogo from "./resources/images/Merki-a-vef-skjoldur-vinstr.jpg"
import visLogo from "./resources/images/vislogo.png"
import origoLogo from "./resources/images/origologo.png"
import tolvutekLogo from "./resources/images/tolvutekLogo.jpg"
import akureyriLogo from "./resources/images/akureyriLogo.png"
import goggLogo from "./resources/images/goggLogo.svg"
import felagsbustadirLogo from "./resources/images/felagsbustadirLogo.png"
import _360wlogo from "./resources/images/360w-logo.png"
import samkaupLogo from "./resources/images/samkaupLogo.jpg"
import dalvikLogo from "./resources/images/dalvikLogo.gif"
import kpmgLogo from "./resources/images/kpmg.png"
import reykjanesbaerLogo from "./resources/images/reykjanesbaerLogo.png"
import unimazeLogo from "./resources/images/unimaze-logo.png"
import brimLogo from "./resources/images/brim-logo-1.png"
import milaLogo from "./resources/images/MILA_logo.png"
import fjardabyggdLogo from "./resources/images/fjardabyggd-logo.png"
import eflaLogo from "./resources/images/efla-logo.png"
import fylkirLogo from "./resources/images/fylkir-logo.png"
import ofarlausnirLogo from "./resources/images/Ofar_lausnir_logo.png"
import thjodkirkjanLogo from "./resources/images/Thjodkirkjan.png"
import novaLogo from "./resources/images/nova-logo.png"
import hiLogo from "./resources/images/hilogo.jpg"
import slokkvilidLogo from "./resources/images/slokkvilidlogo.jpg"
import alpLogo from "./resources/images/alpLogo.png"

import CreateInvoice from "@unimaze/billing/lib/create-invoice/IntlLoader";
import {NewReceiverRequirements} from "@unimaze/billing/lib/common/types/NewReceiverRequirements";
import ComponentContainer from "@unimaze/billing/lib/common/components/component-container";

import axios from "axios";
import {PaymentMeansType} from "@unimaze/api-clients/lib/mapi";

declare global {
    interface Window {
        env: {
            environmentConfiguration: EnvironmentConfiguration[];
        };
    }
}

interface ProfileOutputWithApiKey extends ProfileOutput {
    apiKey: string;
}

interface EnvironmentConfiguration {
    host: string;
    mapiUrl: string;
    rapiUrl: string;
    rapiApiKey: string;
    language: "is" | "en";
    infoText?: string;
    supportEmail?: string;
    uploadSizeLimit: number;
    attachmentTooltipText?: { is: string, en: string };
    referenceIdentifiersHelpText?: string;
    referenceIdentifiersLabelText?: string;
    dueDateMinimumOffset?: number;
    customers: ProfileOutputWithApiKey[];
    defaultPaymentType?: string; // NBNB: cast to enum before using
}

const App = () => {
    const [isLoadingRequirements, setIsLoadingRequirements] = useState<boolean>(false);
    const [receiverRequirements, setReceiverRequirements] = useState<NewReceiverRequirements>(undefined);

    const configuration = window.env.environmentConfiguration;
    // NBNB: HOSTING CONFIGURATION WAS MOVED TO THE env.js FILE THAT IS LOCATED IN ./public/env.js. MAKE ALL CHANGES TO THE CONFIGURATION IN THAT FILE

    const getEnvironmentConfiguration = (config: EnvironmentConfiguration[]): EnvironmentConfiguration => {
        const hostname = window.location.hostname;
        const correctEnv = config.find(element => element.host === hostname);

        if (correctEnv) {
            return correctEnv;
        } else {
            message.error("Error retrieving configuration")
            throw new Error("Error retrieving configuration")
        }
    }

    const environmentConfiguration = getEnvironmentConfiguration(configuration);
    const {
        customers,
        infoText,
        mapiUrl,
        rapiUrl,
        supportEmail,
        language,
        uploadSizeLimit,
        attachmentTooltipText,
        rapiApiKey,
        referenceIdentifiersLabelText,
        referenceIdentifiersHelpText,
        dueDateMinimumOffset,
        defaultPaymentType
    } = environmentConfiguration;

    document.title = language === "is" ? "Senda reikning" : "Send invoice";

    const getLogo = (host: string) => {
        switch (host) {
            case "sjova.localhost":
            case "sjova-billing.unimaze.com":
            case "sjova-billing-test.unimaze.com":
                return sjovalogorgb_medium;
            case "rek.localhost":
            case "reykjavik-billing.unimaze.com":
            case "reykjavik-billing-test.unimaze.com":
                return reklogo;
            case "vis.localhost":
            case "vis-billing.unimaze.com":
            case "vis-billing-test.unimaze.com":
                return visLogo;
            case "origo.localhost":
            case "origo-billing.unimaze.com":
            case "origo-billing-test.unimaze.com":
                return origoLogo;
            case "tolvutek.localhost":
            case "tolvutek-billing.unimaze.com":
            case "tolvutek-billing-test.unimaze.com":
                return tolvutekLogo;
            case "akureyri.localhost":
            case "akureyri-billing.unimaze.com":
            case "akureyri-billing-test.unimaze.com":
                return akureyriLogo;
            case "gogg-billing.unimaze.com":
                return goggLogo;
            case "felagsbustadir-billing.unimaze.com":
                return felagsbustadirLogo;
            case "360w-billing-test.unimaze.com":
            case "360-wanderlust-billing.unimaze.com":
                return _360wlogo;
            case "samkaup-billing-test.unimaze.com":
            case "samkaup-billing.unimaze.com":
                return samkaupLogo;
            case "dalvikurbyggd-billing-test.unimaze.com":
            case "dalvikurbyggd-billing.unimaze.com":
                return dalvikLogo;
            case "kpmg-billing-test.unimaze.com":
            case "kpmg-billing.unimaze.com":
                return kpmgLogo;
            case "reykjanesbaer-billing-test.unimaze.com":
            case "reykjanesbaer-billing.unimaze.com":
                return reykjanesbaerLogo;
            case "localhost":
            case "billing-test.unimaze.com":
            case "billing.unimaze.com":
                return unimazeLogo;
			case "brim-billing-test.unimaze.com":
            case "brim-billing.unimaze.com":
                return brimLogo;
			case "mila-billing-test.unimaze.com":
            case "mila-billing.unimaze.com":
                return milaLogo;
			case "fjardabyggd-billing-test.unimaze.com":
            case "fjardabyggd-billing.unimaze.com":
                return fjardabyggdLogo;
			case "efla-billing-test.unimaze.com":
            case "efla-billing.unimaze.com":
                return eflaLogo;
            case "fylkir-billing.unimaze.com":
                return fylkirLogo;
            case "ofarlausnir-billing.unimaze.com":
            case "ofarlausnir-billing-test.unimaze.com":
                return ofarlausnirLogo;
            case "thjodkirkjan-billing.unimaze.com":
                return thjodkirkjanLogo;
            case "nova-billing.unimaze.com":
                return novaLogo;
            case "hi-billing.unimaze.com":
                return hiLogo;
            case "slokkvilid-billing.unimaze.com":
                return slokkvilidLogo;     
            case "alp-billing.unimaze.com":
                return alpLogo;                          
            default:
                return undefined
        }
    }

    const getReceiverRequirements = (rapiUrl: string, participantIdentifier: string): void => {
        setIsLoadingRequirements(true);
        setReceiverRequirements(undefined);
        axios.get(`${rapiUrl}/api/v1/requirements/${participantIdentifier}`, {
            headers: {
                "Accept-Language": environmentConfiguration.language,
            }
        })
            .then(response => {
                setReceiverRequirements(response.data);
                setIsLoadingRequirements(false);
            })
            .catch(error => {
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    message.error(error.response.statusText);
                    // message.error(error.response.data.message);
                } else if (error.request) {
                    // The request was made but no response was received
                    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                    // http.ClientRequest in node.js
                    const request: XMLHttpRequest = error.request;
                    // tslint:disable-next-line:no-console
                    console.log(error.request);
                    message.error(request.responseText);
                } else {
                    // Something happened in setting up the request that triggered an Error
                    message.error(error.message);
                }
                // tslint:disable-next-line:no-console
                console.log(error.config);
                // message.error()
                setReceiverRequirements(undefined);
                setIsLoadingRequirements(false);
            })
    }

    useEffect(() => {
        getReceiverRequirements(rapiUrl, customers[0].participantLegalIsoIdentifier)
    }, []);

    const supportText = language === "is" ? "Hafa samband:" : "Contact:";
    const termsOfUseText = language === "is" ? "Skilmálar" : "Terms of use";
    return (
        <Layout>
            {/*<Layout.Header className={"header_with_border"}>*/}
            {/*    <img src={getLogo(window.location.hostname)} height={"64px"} alt={""}/>*/}
            {/*</Layout.Header>*/}
            <Layout.Content style={{padding: "16px"}}>
                {<>
                    <ComponentContainer noHeader>
                        <div style={{display: "flex", flexFlow: "row nowrap", justifyContent: "flex-start", alignItems: "flex-start", alignContent: "flex-start"}}>
                            <div style={{order: 1, flex: "0 1 auto"}}><img src={getLogo(window.location.hostname)} height={"64px"} alt={""}/></div>
                            {
                                infoText
                                    ? <div style={{marginLeft: "16px", order: 2, flex: "0 1 auto"}} dangerouslySetInnerHTML={{__html: infoText}}/>
                                    : null
                            }
                        </div>
                    </ComponentContainer>
                    <br/>
                </>
                }
                <Spin spinning={isLoadingRequirements}>
                    <CreateInvoice
                        language={language}
                        mapiBaseUrl={mapiUrl}
                        rapiBaseUrl={rapiUrl}
                        rapiApiKey={rapiApiKey}
                        customers={customers}
                        getReceiverRequirements={getReceiverRequirements}
                        receiverRequirements={receiverRequirements}
                        updateReceiverRequirementsProp={setReceiverRequirements}
                        uploadSizeLimit={uploadSizeLimit}
                        attachmentTooltipText={attachmentTooltipText}
                        referenceIdentifiersLabelText={referenceIdentifiersLabelText}
                        referenceIdentifiersHelpText={referenceIdentifiersHelpText}
                        dueDateMinimumOffset={dueDateMinimumOffset}
                        defaultPaymentType={PaymentMeansType[defaultPaymentType]}
                    />
                </Spin>
            </Layout.Content>
            <Layout.Footer className={"footer_with_border"}>
                <Row>
                    <Col span={8}>
                        <a href={"https://www.unimaze.com/is"}><img src={poweredByUnimazeLogo} height={"32px"} alt={"Powered by Unimaze"}/></a>
                    </Col>
                    <Col span={8}>
                        <div style={{width: "100%", textAlign: "center", lineHeight: "32px"}}>{supportText} <a href={`mailto:${supportEmail}`}>{supportEmail}</a></div>
                    </Col>
                    <Col span={8}>
                        <div style={{width: "100%", textAlign: "right", lineHeight: "32px"}}><a href={"https://www.unimaze.com/terms-of-use/"} target="_blank">{termsOfUseText}</a></div>
                    </Col>
                </Row>
            </Layout.Footer>
        </Layout>
    );
};

export default App;
